import React, { useCallback, useEffect, useState } from "react";
import {
  Copy,
  Download,
  ExternalLink,
  Loader2,
  Pencil,
  Plus,
  QrCode,
  RefreshCw,
  X,
} from "lucide-react";
import { toast } from "react-toastify";
import PhoneSelector from "../components/PhoneSelector";
import QrImage, { downloadQrPng } from "../components/QrImage";
import { usePhoneAccount } from "../contexts/PhoneAccountContext";
import { createQrCode, listQrCodes, updateQrCode } from "../apis/qrCodes";

function apiError(err, fallback) {
  return (
    err?.response?.data?.error ||
    err?.response?.data?.details ||
    err?.response?.data?.message ||
    err?.message ||
    fallback
  );
}

function normalizeQr(item) {
  if (!item || typeof item !== "object") return null;
  const code = item.code || item.qrCodeId || item.qr_code_id || item.id || "";
  if (!code) return null;
  const deepLinkUrl =
    item.deep_link_url ||
    item.deepLinkUrl ||
    item.deep_link ||
    `https://wa.me/message/${code}`;
  return {
    code,
    prefilledMessage: item.prefilled_message || item.prefilledMessage || "",
    deepLinkUrl,
  };
}

const QrCodes = () => {
  const { selectedPhoneId } = usePhoneAccount();
  const [qrCodes, setQrCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createMessage, setCreateMessage] = useState("");
  const [createFormat, setCreateFormat] = useState("PNG");
  const [editing, setEditing] = useState(null);
  const [editMessage, setEditMessage] = useState("");
  const [saving, setSaving] = useState(false);

  const loadQrCodes = useCallback(async () => {
    if (!selectedPhoneId) {
      setQrCodes([]);
      return;
    }
    setLoading(true);
    try {
      const res = await listQrCodes(selectedPhoneId);
      const items = (res.data?.qrCodes || []).map(normalizeQr).filter(Boolean);
      setQrCodes(items);
    } catch (err) {
      console.error("Failed to load QR codes:", err);
      toast.error(apiError(err, "Failed to load QR codes"));
      setQrCodes([]);
    } finally {
      setLoading(false);
    }
  }, [selectedPhoneId]);

  useEffect(() => {
    loadQrCodes();
  }, [loadQrCodes]);

  const handleCreate = async (e) => {
    e.preventDefault();
    const prefilledMessage = createMessage.trim();
    if (!prefilledMessage) {
      toast.error("Enter a prefilled message");
      return;
    }
    setCreating(true);
    try {
      await createQrCode(selectedPhoneId, {
        prefilledMessage,
        generateQrImage: createFormat,
      });
      toast.success("QR code created");
      setShowCreate(false);
      setCreateMessage("");
      setCreateFormat("PNG");
      await loadQrCodes();
    } catch (err) {
      toast.error(apiError(err, "Failed to create QR code"));
    } finally {
      setCreating(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editing) return;
    const prefilledMessage = editMessage.trim();
    if (!prefilledMessage) {
      toast.error("Enter a prefilled message");
      return;
    }
    setSaving(true);
    try {
      await updateQrCode(selectedPhoneId, {
        qrCodeId: editing.code,
        prefilledMessage,
      });
      toast.success("QR code updated");
      setEditing(null);
      setEditMessage("");
      await loadQrCodes();
    } catch (err) {
      toast.error(apiError(err, "Failed to update QR code"));
    } finally {
      setSaving(false);
    }
  };

  const closeDrawer = () => {
    setShowCreate(false);
    setEditing(null);
  };

  const copyLink = async (url) => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy link");
    }
  };

  return (
    <div className="min-h-screen bg-[#fff]">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-5 py-4">
        <div className="mb-4">
          <PhoneSelector section="phone_numbers" />
        </div>

        <div className="page-head" style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18, flexWrap: "wrap" }}>
          <div>
            <h2 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>QR Codes & Short Links</h2>
            <p style={{ color: "var(--text-2)", fontSize: 14, marginTop: 2 }}>
              Create WhatsApp QR codes with a prefilled message
            </p>
          </div>
          <div style={{ flex: 1 }} />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={loadQrCodes}
              disabled={!selectedPhoneId || loading}
              className="ping-btn ping-btn-ghost ping-btn-sm disabled:opacity-40"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
              Refresh
            </button>
            <button
              type="button"
              onClick={() => {
                setEditing(null);
                setShowCreate(true);
              }}
              disabled={!selectedPhoneId}
              className="ping-btn ping-btn-primary ping-btn-sm disabled:opacity-40"
            >
              <Plus size={16} />
              New QR Code
            </button>
          </div>
        </div>

        {!selectedPhoneId ? (
          <div className="rounded-2xl border border-gray-200 bg-gray-50 px-6 py-16 text-center">
            <QrCode className="mx-auto mb-3 text-gray-400" size={36} />
            <p className="text-sm text-gray-600">Select a WhatsApp number to manage QR codes.</p>
          </div>
        ) : loading && qrCodes.length === 0 ? (
          <div className="flex items-center justify-center py-20 text-gray-500">
            <Loader2 className="animate-spin mr-2" size={20} />
            Loading QR codes…
          </div>
        ) : qrCodes.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-gray-300 bg-white px-6 py-16 text-center">
            <QrCode className="mx-auto mb-3 text-gray-400" size={36} />
            <p className="text-sm font-medium text-gray-800">No QR codes yet</p>
            <p className="text-sm text-gray-500 mt-1">
              Create one with a prefilled WhatsApp message.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {qrCodes.map((qr) => (
              <div
                key={qr.code}
                className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm"
              >
                <div className="flex items-start gap-4">
                  <div className="w-28 h-28 shrink-0 rounded-xl border border-gray-100 bg-white overflow-hidden flex items-center justify-center p-1">
                    <QrImage value={qr.deepLinkUrl} size={104} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-mono text-gray-500 truncate">{qr.code}</p>
                    <p className="mt-1 text-sm font-medium text-gray-900 line-clamp-3">
                      {qr.prefilledMessage || "—"}
                    </p>
                    {qr.deepLinkUrl && (
                      <a
                        href={qr.deepLinkUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-2 flex items-center gap-1 text-xs text-emerald-700 hover:underline min-w-0"
                      >
                        <span className="truncate">{qr.deepLinkUrl}</span>
                        <ExternalLink size={12} className="shrink-0" />
                      </a>
                    )}
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => copyLink(qr.deepLinkUrl)}
                    disabled={!qr.deepLinkUrl}
                    className="ping-btn ping-btn-ghost ping-btn-sm whitespace-nowrap disabled:opacity-40"
                  >
                    <Copy size={13} />
                    Copy link
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await downloadQrPng(qr.deepLinkUrl, `whatsapp-qr-${qr.code}.png`);
                      } catch {
                        toast.error("Could not download QR");
                      }
                    }}
                    className="ping-btn ping-btn-ghost ping-btn-sm whitespace-nowrap"
                  >
                    <Download size={13} />
                    PNG
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreate(false);
                      setEditing(qr);
                      setEditMessage(qr.prefilledMessage);
                    }}
                    className="ping-btn ping-btn-ghost ping-btn-sm whitespace-nowrap"
                  >
                    <Pencil size={13} />
                    Edit
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {(showCreate || editing) && (
        <>
          <div className="ping-drawer-backdrop" onClick={closeDrawer} />
          <aside className="ping-drawer" role="dialog" aria-modal="true">
            {showCreate ? (
              <form onSubmit={handleCreate} className="flex flex-col h-full">
                <div className="ping-drawer-head">
                  <div>
                    <h3>New QR Code</h3>
                    <p>Add a prefilled WhatsApp message</p>
                  </div>
                  <button type="button" onClick={closeDrawer} className="ping-btn ping-btn-ghost ping-btn-sm">
                    <X size={16} />
                  </button>
                </div>
                <div className="ping-drawer-body">
                  <label htmlFor="qr-create-message">Prefilled message</label>
                  <textarea
                    id="qr-create-message"
                    value={createMessage}
                    onChange={(e) => setCreateMessage(e.target.value)}
                    rows={5}
                    placeholder="Hi, I want to know more about…"
                  />
                  <label style={{ marginTop: 16 }}>QR image format</label>
                  <div className="flex gap-2">
                    {["PNG", "SVG"].map((fmt) => (
                      <button
                        key={fmt}
                        type="button"
                        onClick={() => setCreateFormat(fmt)}
                        className={`ping-ptab${createFormat === fmt ? " on" : ""}`}
                      >
                        {fmt}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="ping-drawer-foot">
                  <button type="button" onClick={closeDrawer} className="ping-btn ping-btn-ghost ping-btn-sm">
                    Cancel
                  </button>
                  <button type="submit" disabled={creating} className="ping-btn ping-btn-primary ping-btn-sm disabled:opacity-50">
                    {creating ? "Creating…" : "Create"}
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleUpdate} className="flex flex-col h-full">
                <div className="ping-drawer-head">
                  <div>
                    <h3>Edit QR Code</h3>
                    <p>{editing.code}</p>
                  </div>
                  <button type="button" onClick={closeDrawer} className="ping-btn ping-btn-ghost ping-btn-sm">
                    <X size={16} />
                  </button>
                </div>
                <div className="ping-drawer-body">
                  <label htmlFor="qr-edit-message">Prefilled message</label>
                  <textarea
                    id="qr-edit-message"
                    value={editMessage}
                    onChange={(e) => setEditMessage(e.target.value)}
                    rows={5}
                  />
                </div>
                <div className="ping-drawer-foot">
                  <button type="button" onClick={closeDrawer} className="ping-btn ping-btn-ghost ping-btn-sm">
                    Cancel
                  </button>
                  <button type="submit" disabled={saving} className="ping-btn ping-btn-primary ping-btn-sm disabled:opacity-50">
                    {saving ? "Saving…" : "Save"}
                  </button>
                </div>
              </form>
            )}
          </aside>
        </>
      )}
    </div>
  );
};

export default QrCodes;
